import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import SmallCard from '@/components/SmallCard';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import AvgAckTimeWidget from '@/features/AvgAckTimeWidget';
import { getMetricTimes } from '@/pages/ReportsTemplates/MDR/ReportsPages/TicketsPages/TicketsPages.utils';
import type { TicketMetricsByPriority } from '@/pages/ReportsTemplates/ReportsTemplate.types';

import { useFetchStatusTimes } from '../../Dashboard.hooks';
import dashboardTimeFrameAtom from '../../Dashboard.store';
import type { TicketsAckTimesDTO } from '../../Dashboard.types';
import { widgetSearchParams } from '../../Dashboard.utils';
import type { TicketsSmallCardsProps } from './TicketsSmallCards.types';

const TicketsSmallCards = (props: TicketsSmallCardsProps) => {
  const {
    classes,
    selectedOrganization,
    timeFilter,
    threatOpenTicketsCount,
    threatHighPriorityTicketsCount,
    threatAwaitingRemTicketsCount
  } = props;
  const navigate = useNavigate();

  const dashboardTimeFrame = useAtomValue(dashboardTimeFrameAtom);

  const { data: allMetrics } = useFetchStatusTimes(
    dayjs().subtract(dashboardTimeFrame, 'day').format(YEAR_MONTH_DAY_FORMAT),
    dayjs().add(1, 'day').format(YEAR_MONTH_DAY_FORMAT) // using current date was causing an issue with the test query, likely due to timezones
  );

  const mttdMetrics = getMetricTimes(
    'mttd',
    allMetrics as TicketMetricsByPriority[]
  );
  const mttaMetrics = getMetricTimes(
    'mtta',
    allMetrics as TicketMetricsByPriority[]
  );
  const mttnMetrics = getMetricTimes(
    'mttn',
    allMetrics as TicketMetricsByPriority[]
  );

  const handleCardClick = (title: string) => {
    navigate({
      pathname: '/tickets',
      search: widgetSearchParams(title, timeFilter).toString()
    });
  };

  return (
    <>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          onCardClick={handleCardClick}
          color="error"
          title="High Priority Threat Tickets"
          primaryContent={threatHighPriorityTicketsCount}
          tooltipText="All incomplete P1 and P2 tickets"
        />
      </div>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          onCardClick={handleCardClick}
          color="warning"
          title="Open Threat Tickets"
          primaryContent={threatOpenTicketsCount}
          tooltipText="Need Acknowledgment"
        />
      </div>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          onCardClick={handleCardClick}
          title="Unresolved Threat Tickets"
          primaryContent={threatAwaitingRemTicketsCount}
          tooltipText="All Open and Acknowledged tickets"
        />
      </div>
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Avg. Detection Time"
          primaryContent={
            selectedOrganization.id > 0 ? (
              <AvgAckTimeWidget
                showAllPriorities={false}
                allPrioritiesAckTimes={mttdMetrics as TicketsAckTimesDTO[]}
              />
            ) : (
              <div style={{ fontSize: '1rem' }}>Select an organization</div>
            )
          }
          tooltipText="MTTD"
        />
      </div>
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Avg. Escalation Time"
          primaryContent={
            selectedOrganization.id > 0 ? (
              <AvgAckTimeWidget
                showAllPriorities={false}
                allPrioritiesAckTimes={mttnMetrics as TicketsAckTimesDTO[]}
              />
            ) : (
              <div style={{ fontSize: '1rem' }}>Select an organization</div>
            )
          }
          tooltipText="MTTN"
        />
      </div>
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Avg. Acknowledgement Time"
          primaryContent={
            selectedOrganization.id > 0 ? (
              <AvgAckTimeWidget
                showAllPriorities={false}
                allPrioritiesAckTimes={mttaMetrics as TicketsAckTimesDTO[]}
              />
            ) : (
              <div style={{ fontSize: '1rem' }}>Select an organization</div>
            )
          }
          tooltipText="MTTA"
        />
      </div>
    </>
  );
};

TicketsSmallCards.displayName = 'TicketsSmallCards';

export default TicketsSmallCards;
